/**
 * supabaseUsers.ts — CRUD-Schicht für `public.users` (Berater-Profil).
 *
 * Trennung der Verantwortung:
 *   - Auth-State liefert `useAuthOrSession()` (id + email).
 *   - Profil-Stammdaten (Briefkopf, Anrede, Titel, Signatur) liegen in
 *     `public.users` und werden hier gelesen/geschrieben.
 *
 * RLS aus 0004_policies.sql:
 *   - users_self_select / users_self_update: jeder User darf seine eigene
 *     Zeile lesen und ändern. WITH CHECK verhindert Cross-User-Updates.
 *   - 0009 hat die Briefkopf-Felder als ALTER TABLE … ADD COLUMN ergänzt.
 *
 * Storage:
 *   - Signatur-Upload geht in den Bucket `signatures` (Migration 0010),
 *     Pfad-Format: `{user_id}/{filename}`. Storage-RLS erzwingt, dass das
 *     erste Pfadsegment = auth.uid() ist.
 */

import { getSupabaseClient } from './supabase';
import type { Database, UserRow } from './supabaseTypes';

export type UserProfileRow = UserRow;

export interface UpdateProfileInput {
  readonly displayName?: string | null;
  readonly salutation?: string | null;
  readonly title?: string | null;
  readonly providerName?: string | null;
  readonly providerAddressLine1?: string | null;
  readonly providerAddressLine2?: string | null;
  readonly providerPhoneEmail?: string | null;
  readonly certifications?: string | null;
  readonly signatureUrl?: string | null;
}

const SIGNATURE_BUCKET = 'signatures';
const MAX_SIGNATURE_BYTES = 2 * 1024 * 1024; // 2 MiB
const SIGNATURE_MIME_WHITELIST: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpeg',
  'image/svg+xml',
]);

/**
 * Liest das Profil des eingeloggten Users. Gibt `null` zurück, wenn
 * (a) kein User eingeloggt ist oder (b) der Provisioning-Trigger noch nicht
 * gefeuert hat (sehr kurzes Zeitfenster direkt nach Sign-Up).
 */
export async function getProfile(): Promise<UserProfileRow | null> {
  const supa = getSupabaseClient();
  const { data: userData, error: userErr } = await supa.auth.getUser();
  if (userErr) {
    console.error('getProfile: auth.getUser failed', userErr);
    return null;
  }
  const uid = userData.user?.id;
  if (!uid) return null;

  const { data, error } = await supa.from('users').select('*').eq('id', uid).maybeSingle();
  if (error) {
    console.error('getProfile failed', {
      uid,
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code,
    });
    throw new Error(`getProfile [${error.code ?? '?'}]: ${error.message}`);
  }
  return data;
}

/**
 * Aktualisiert das Profil des eingeloggten Users. Nur explizit übergebene
 * Felder werden geändert (Partial-Update). E-Mail und Rolle sind tabu —
 * E-Mail kommt aus auth.users (Magic-Link), Rolle wird per Admin gesetzt.
 */
export async function updateProfile(input: UpdateProfileInput): Promise<UserProfileRow> {
  const supa = getSupabaseClient();
  const { data: userData } = await supa.auth.getUser();
  const uid = userData.user?.id;
  if (!uid) throw new Error('updateProfile: kein authentifizierter User');

  const update: Database['public']['Tables']['users']['Update'] = {};
  if (input.displayName !== undefined) update.display_name = input.displayName;
  if (input.salutation !== undefined) update.salutation = input.salutation;
  if (input.title !== undefined) update.title = input.title;
  if (input.providerName !== undefined) update.provider_name = input.providerName;
  if (input.providerAddressLine1 !== undefined)
    update.provider_address_line1 = input.providerAddressLine1;
  if (input.providerAddressLine2 !== undefined)
    update.provider_address_line2 = input.providerAddressLine2;
  if (input.providerPhoneEmail !== undefined)
    update.provider_phone_email = input.providerPhoneEmail;
  if (input.certifications !== undefined) update.certifications = input.certifications;
  if (input.signatureUrl !== undefined) update.signature_url = input.signatureUrl;

  const { data, error } = await supa
    .from('users')
    .update(update as never)
    .eq('id', uid)
    .select('*')
    .single();
  if (error) {
    console.error('updateProfile failed', {
      uid,
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code,
      payload: update,
    });
    const detail = error.details ? ` · ${error.details}` : '';
    throw new Error(`updateProfile [${error.code ?? '?'}]: ${error.message}${detail}`);
  }
  return data;
}

export interface SignatureUploadResult {
  readonly path: string;
  readonly publicUrl: string;
}

/**
 * Lädt eine Signatur-Datei in den `signatures`-Bucket. Validiert MIME-Type
 * und Größe clientseitig — Storage-Policy validiert serverseitig nochmal.
 *
 * Strategie: bisheriger Upload wird überschrieben (`upsert: true`), der
 * Dateiname enthält einen Cache-Buster (Date.now()), damit die alte Version
 * sofort aus dem CDN-Cache fliegt.
 */
export async function uploadSignature(file: File): Promise<SignatureUploadResult> {
  if (file.size > MAX_SIGNATURE_BYTES) {
    throw new Error(
      `Signatur ist zu groß (${Math.round(file.size / 1024).toString()} KB). Maximum: 2 MB.`,
    );
  }
  if (!SIGNATURE_MIME_WHITELIST.has(file.type)) {
    throw new Error(`Format „${file.type}" wird nicht unterstützt. Erlaubt: PNG, JPEG, SVG.`);
  }

  const supa = getSupabaseClient();
  const { data: userData } = await supa.auth.getUser();
  const uid = userData.user?.id;
  if (!uid) throw new Error('uploadSignature: kein authentifizierter User');

  const ext = guessExtension(file.type);
  const filename = `${Date.now().toString()}.${ext}`;
  const path = `${uid}/${filename}`;

  const { error: upErr } = await supa.storage.from(SIGNATURE_BUCKET).upload(path, file, {
    contentType: file.type,
    upsert: true,
    cacheControl: '3600',
  });
  if (upErr) {
    console.error('uploadSignature failed', upErr);
    throw new Error(`uploadSignature: ${upErr.message}`);
  }

  // Bucket ist privat → wir nutzen einen langlebigen signed URL.
  // Im Briefkopf der Stellungnahme wird der Pfad direkt gerendert (DOCX
  // bettet das Bild ein), für die Live-Vorschau im Profil reicht der URL.
  const { data: signed, error: signErr } = await supa.storage
    .from(SIGNATURE_BUCKET)
    .createSignedUrl(path, 60 * 60 * 24 * 365); // 1 Jahr
  if (signErr || !signed) {
    console.error('uploadSignature: createSignedUrl failed', signErr);
    throw new Error(`uploadSignature signed URL: ${signErr?.message ?? 'unknown'}`);
  }

  return { path, publicUrl: signed.signedUrl };
}

function guessExtension(mime: string): string {
  switch (mime) {
    case 'image/png':
      return 'png';
    case 'image/jpeg':
      return 'jpg';
    case 'image/svg+xml':
      return 'svg';
    default:
      return 'bin';
  }
}
